// SubscriptionsPage — what you hold.
//
// The JSON twin of subscriptions/subscription_list.html, and the index of the
// three flows that follow it. The design drop has no screen for this: its Home
// dashboard is the entry point and its wizard ends there. But the templates do
// have this page, and every journey they cover has to be reachable in the
// portal — so this is composed from design-system components in the drop's
// idiom (page header, card, table) rather than designed, and it is one of the
// screens flagged for the async design pass.
import { useCallback } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from '@miq/design-system/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@miq/design-system/components/ui/table';

import { StateBadge } from '../components/StateBadge';
import { onDay } from '../data/instants';
import { usePortalSession } from '../auth/context';
import { fetchSubscriptions } from '../data/subscriptions';
import { useLoaded } from '../data/useLoaded';
import { useDocumentTitle } from '../../lib/documentTitle';

export function SubscriptionsPage({
  onOpen,
}: {
  onOpen: (publicId: string) => void;
}) {
  useDocumentTitle('Subscriptions');
  const { authorizedFetch } = usePortalSession();
  const read = useCallback(() => fetchSubscriptions(authorizedFetch), [authorizedFetch]);
  const { state } = useLoaded(read);

  return (
    <div className="space-y-5 p-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Commerce · Subscriptions
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Subscriptions</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          What you have bought from us, and where each one stands. Billing and
          payment stay with the sales channel that sold it.
        </p>
      </div>

      {state.status === 'loading' && <Placeholder>Reading your subscriptions…</Placeholder>}

      {state.status === 'refused' && (
        <Placeholder tone="destructive">{state.refusal}</Placeholder>
      )}

      {state.status === 'loaded' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Agreements</CardTitle>
            <CardDescription>
              {state.data.subscriptions.length} · {state.data.customer.display_name}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {state.data.subscriptions.length === 0 ? (
              // Zero rows is a state, not a blank. An account that has bought
              // nothing has to read as that rather than as a page that failed.
              <p className="px-6 py-8 text-sm text-muted-foreground">
                Nothing here yet. A subscription appears once the sales channel
                you bought through hands it over to us.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agreement</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {state.data.subscriptions.map((subscription) => (
                    <TableRow key={subscription.public_id}>
                      <TableCell>
                        <div className="font-medium">{subscription.agreement_ref}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {subscription.public_id}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {subscription.channel}
                      </TableCell>
                      <TableCell>
                        <StateBadge subscription={subscription} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {onDay(subscription.started_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onPress={() => onOpen(subscription.public_id)}
                          >
                            Open <ChevronRight className="size-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Placeholder({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: 'destructive';
}) {
  return (
    <Card>
      <CardContent className="py-8">
        <p
          role={tone === 'destructive' ? 'alert' : undefined}
          className={`text-sm ${
            tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground'
          }`}
        >
          {children}
        </p>
      </CardContent>
    </Card>
  );
}
